/**
 * Generic hook for entity list pages in the Manage Hub.
 * Handles fetching, pagination, search filtering, and CRUD state.
 */

import { useCallback, useEffect, useState } from 'react';

export default function useEntityList(fetchFn, { search = '', showDeleted = false, pageSize = 200, extraParams = {} } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        search: search || undefined,
        include_deleted: showDeleted || undefined,
        ...extraParams,
      };
      // Remove undefined keys
      Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
      const response = await fetchFn(params);
      // Handle paginated responses: { data: [...], count: N } or { results: [...], count: N }
      if (response && Array.isArray(response.data)) {
        setItems(response.data);
        setTotalCount(response.count || response.data.length);
      } else if (response && Array.isArray(response.results)) {
        setItems(response.results);
        setTotalCount(response.count || response.results.length);
      } else if (Array.isArray(response)) {
        setItems(response);
        setTotalCount(response.length);
      } else {
        setItems([]);
        setTotalCount(0);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, page, search, showDeleted, JSON.stringify(extraParams)]);

  useEffect(() => { load(); }, [load]);

  // Reset page when search changes
  useEffect(() => { setPage(1); }, [search, showDeleted]);

  return { items, loading, totalCount, page, setPage, refresh: load };
}
